import { useEffect, useState, useMemo } from "react";
import {
    AlertColor,
    Box,
    Button,
    Divider,
    FormControlLabel,
    IconButton,
    Grid2 as Grid,
    Switch,
    Tooltip,
    Typography,
} from "@mui/material";
import { useTemplatesDataStore } from "../../../../config/advertiser.stores";
import { FormTextField, FormSelect } from "../../../Shared/Form";
import { HighlightOffTwoTone, InfoOutlined } from '@mui/icons-material';
import TemplatePreview from "../../../Shared/TemplatePreview";
import adService from "../../../../services/ad.service";
import { CreateCampaignViewModelType } from "../ViewModel";
import { Template } from "../../../../types/template";

type MediaPageType = {
    viewModel: CreateCampaignViewModelType;
    setNextStepDisabled: (v: boolean) => void;
    setSnackbar: (v: string) => void;
    setSeverity: (v: AlertColor) => void;
}

const maxFileSize = 300 * 1024 * 1024; // 300MB

const MediaPage = ({ viewModel, setNextStepDisabled, setSnackbar, setSeverity }: MediaPageType) => {
    const {
        objective,
        media,
        setMedia,
        link,
        setLink,
        title,
        setTitle,
        headline,
        setHeadline,
        nickname,
        setNickname,
        templateId,
        setTemplateId,
        setMediaChanged,
    } = viewModel;

    const [showPreview, setShowPreview] = useState(true);
    const templatesData = useTemplatesDataStore((state) => state.templatesData);
    const setTemplatesData = useTemplatesDataStore((state) => state.setTemplatesData);

    function setDefaultTemplate(templates: Template[]) {
        if (objective === 2) {
            setTemplateId(templates.find(template => template.orientation === "portrait")?.id ?? -1);
        } else {
            setTemplateId(templates.find(template => template.orientation === "landscape")?.id ?? -1);
        }
    }

    async function fetchTemplates() {
        const response = await adService.queryTemplate();
        setTemplatesData(response);
        if (response.length === 0) {
            setSeverity("error");
            setSnackbar("There was an error loading templates. Please try again later.");
        } else if (templateId === -1) {
            setDefaultTemplate(response);
        }
    };

    useEffect(() => {
        if (templatesData.length === 0) { 
            fetchTemplates();
        } else if (templateId === -1) {
            setDefaultTemplate(templatesData);
        }
        // eslint-disable-next-line
    }, []);

    const handleMediaInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files ?? [];
        if (files.length > 3) {
            setSeverity("error");
            setSnackbar("You can only upload up to 3 files.");
        } else if (files.length >= 1 && (objective === 2 || objective === 0) && files[0].type.startsWith("video")) {
            setSeverity("error");
            setSnackbar("For ads that appear on the Explore Page, make sure your first media is not" +
                " a video as it will be used as a thumbnail.");
        } else {
            var newFiles = Array.from(files).filter((file) => file.size <= maxFileSize);
            if (newFiles.length < files.length) {
                setSeverity("warning");
                setSnackbar("Files that are larger than 300MB were omitted.");
            }

            setMedia(newFiles);
            setMediaChanged(true);
        }
        event.target.value = "";
    };

    const handleRemoveImage = (file: File) => {
        setMedia(media.filter((f) => f !== file));
    };

    const getTemplateOptions = useMemo(() => {
        return templatesData.filter(
            template => template.orientation === (objective === 2 ? "portrait" : "landscape")
        );
    }, [templatesData, objective]);

    const getTemplatePreview = useMemo(() => {
        let template = templatesData.find(t => t.id === templateId)?.html ?? "";
        return template;
    }, [templatesData, templateId]);

    const [titleError, setTitleError] = useState("");
    const [headlineError, setHeadlineError] = useState("");
    const [nicknameError, setNicknameError] = useState("");
    const [linkError, setLinkError] = useState("");

    useEffect(() => {
        if (title.length > 30) {
            setTitleError("Title cannot be more than 30 characters");
        } else {
            setTitleError("");
        }
    }, [title]);

    useEffect(() => {
        if (headline.length > 150) {
            setHeadlineError("Headline cannot be more than 150 characters");
        } else {
            setHeadlineError("");
        }
    }, [headline]);

    useEffect(() => {
        if (nickname.length > 30) {
            setNicknameError("Nickname cannot be more than 30 characters");
        } else {
            setNicknameError("");
        }
    }, [nickname]);

    useEffect(() => {
        if (link.length > 0 && (!link.startsWith("https://") && !link.startsWith("http://"))) {
            setLinkError("Links should begin with https:// or http://");
        } else {
            setLinkError("");
        }
    }, [link]);

    useEffect(() => {
        if (objective !== 2 && templateId === -1) {
            setNextStepDisabled(true);
        } else if (
            media.length === 0 || nickname.length === 0 || link.length === 0 || templateId === -1 ||
            titleError || headlineError || nicknameError || linkError
        ) {
            setNextStepDisabled(true);
        } else {
            setNextStepDisabled(false);
        }
    }, [objective, templateId, nickname, link, media, titleError, headlineError, nicknameError, linkError, setNextStepDisabled])

    return (
        <div style={{ paddingTop: 5, paddingBottom: 5 }}>
            <Box display="flex" gap={2} pt={1}>
                <Box width="100%" display="flex" gap={2} flexDirection="column">
                    <Typography variant="h6">Ad Details</Typography>
                    <Grid container spacing={3} width={"100%"}>
                        <FormTextField
                            label="Nickname"
                            value={nickname}
                            setValue={setNickname}
                            helperText="For your own reference"
                            error={nicknameError}
                            required
                            size={4}
                        />
                        <FormTextField
                            label="Website/Store Link"
                            value={link}
                            setValue={setLink}
                            error={linkError}
                            required
                            size={8}
                        />
                    </Grid>
                    <Grid container width={"100%"}>
                        <FormTextField
                            label="Ad Title"
                            value={title}
                            setValue={setTitle}
                            error={titleError}
                            size={12}
                        />
                    </Grid>
                    <Grid container width={"100%"}>
                        <FormTextField
                            label="Headline"
                            value={headline}
                            setValue={setHeadline}
                            helperText="Provide some details about your ad"
                            error={headlineError}
                            size={12}
                        />
                    </Grid>
                    <Divider />
                    <Typography variant="h6">Upload Media</Typography>
                    <Box display="flex" gap={1} alignItems="center">
                        <Button variant="contained" component="label">
                            Select Media
                            <input
                                multiple
                                accept="image/*,video/*"
                                type="file"
                                onChange={(event) => { handleMediaInput(event) }}
                                hidden
                            />
                        </Button>
                        <Tooltip title={
                            <div>
                                You can upload up to 3 media files.
                                <br />None of the files can exceed 300MB.
                                <br /><br />Make sure files have a valid path extension
                                <br />Images: .jpg, .jpeg, .png
                                <br />Videos: .mp4, .m3u8
                            </div>
                        }>
                            <InfoOutlined />
                        </Tooltip>
                    </Box>
                    {media.length > 0 && 
                        <Box>
                            {media.map((file, index) => (
                                <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
                                    <Typography>{file.name ?? file}</Typography>
                                    <IconButton sx={{ p: 0.5 }} onClick={() => handleRemoveImage(file)}>
                                        <HighlightOffTwoTone fontSize="medium" />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    }
                    { objective !== 2 ? (
                        <>
                            <Typography variant="h6">Select Template</Typography>
                            <FormSelect
                                id="template-select"
                                label="Template"
                                value={templateId === -1 ? "" : templateId}
                                setValue={setTemplateId}
                                items={getTemplateOptions.map(template => template.name)}
                                itemValues={getTemplateOptions.map(template => template.id)}
                                resettable={false}
                            />
                            <Typography>{templatesData.find(t => t.id === templateId)?.description}</Typography>
                            <Box display="flex">
                                <FormControlLabel
                                    control={<Switch checked={showPreview} onChange={(v) => setShowPreview(v.target.checked)} />}
                                    label="Show Preview"
                                />
                            </Box>
                        </>
                    ) : (
                        <>
                            <Typography component="pre">
                                Your first media will be used as the thumbnail in the Explore Page, only images are allowed.{"\n"}
                                Users can view the other media and your ad details after clicking on the ad.
                            </Typography>
                            <FormControlLabel
                                control={<Switch checked={showPreview} onChange={(v) => setShowPreview(v.target.checked)} />}
                                label="Show Preview"
                            />
                        </>
                    )}
                </Box>
                <TemplatePreview
                    template={getTemplatePreview}
                    title={title}
                    headline={headline}
                    media={media}
                    visible={showPreview}
                    inAppPreview={objective !== 2}
                />
            </Box>
        </div>
    )
}

export default MediaPage;
